import React, { useState, useEffect } from 'react';
import { FaUpload, FaVideo, FaCheckCircle, FaTimesCircle, FaChevronLeft, FaBookOpen } from 'react-icons/fa';
import { motion } from 'framer-motion';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';

const StudentHomework = () => {
  const [homeworks, setHomeworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHomework, setSelectedHomework] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchHomeworks();
  }, []);

  const fetchHomeworks = async () => {
    try {
      setLoading(true);
      const res = await api.get('/homework');
      if (res.data.success) {
        setHomeworks(res.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const selectHomework = async (hw) => {
    setSelectedHomework(hw);
    setSubmission(null);
    setFile(null);
    setUploadStatus(null);
    try {
      const res = await api.get(`/homework/${hw._id}/submission`);
      if (res.data.success && res.data.data) {
        setSubmission(res.data.data);
      }
    } catch (err) {
      console.error('No previous submission found or error fetching');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.type.startsWith('video/')) {
        alert('Please select a valid video file');
        return;
      }
      if (selectedFile.size > 5120 * 1024 * 1024) {
        alert('File size must be less than 5GB');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      alert('Please select a video file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('video', file);

      const token = localStorage.getItem('token');
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(progress);
        }
      });

      xhr.onload = function () {
        if (xhr.status === 200) {
          setUploadStatus('success');
          setFile(null);
          const responseData = JSON.parse(xhr.responseText);
          setSubmission(responseData.data);
        } else {
          setUploadStatus('error');
          alert('Upload failed: ' + xhr.responseText);
        }
        setUploading(false);
      };

      xhr.onerror = function () {
        setUploadStatus('error');
        setUploading(false);
      };

      const API_BASE = process.env.REACT_APP_API_URL || '/api';
      xhr.open('POST', `${API_BASE}/homework/${selectedHomework._id}/submit`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formDataToSend);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      setUploading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">Approved</span>;
      case 'rejected': return <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-semibold">Rejected</span>;
      case 'pending': return <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-xs font-semibold">Pending Review</span>;
      default: return null;
    }
  };

  if (loading) return <div className="min-h-screen bg-maroon-dark flex items-center justify-center text-white">Loading...</div>;

  return (
    <div className="min-h-screen bg-maroon-dark text-white p-6 md:p-12 pt-24">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button 
            onClick={() => selectedHomework ? setSelectedHomework(null) : navigate('/dashboard')}
            className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
          >
            <FaChevronLeft className="text-gold" />
          </button>
          <div>
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-gradient-gold">
              {selectedHomework ? 'Submit Assignment' : 'My Homework'}
            </h1>
            <p className="text-gold-light/50 mt-1">
              {selectedHomework ? selectedHomework.title : 'View and submit your assigned practice tasks'}
            </p>
          </div>
        </div>

        {!selectedHomework ? (
          /* List of Homeworks */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {homeworks.length === 0 ? (
              <p className="text-gold-light/50 col-span-3 text-center py-10">You have no tasks assigned right now.</p>
            ) : (
              homeworks.map(hw => (
                <div 
                  key={hw._id}
                  onClick={() => selectHomework(hw)}
                  className="bg-white/5 border border-gold/10 rounded-xl p-6 hover:bg-white/10 cursor-pointer transition-all group"
                >
                  <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <FaBookOpen className="text-2xl text-gold" />
                  </div>
                  <h3 className="text-xl font-bold text-gold-light mb-2">{hw.title}</h3>
                  <p className="text-gold-light/60 text-sm line-clamp-2 mb-4">{hw.description}</p>
                  <div className="flex justify-between items-center text-xs text-gold-light/40">
                    <span>{hw.lessonId ? `Lesson ${hw.lessonId.lessonNumber}` : new Date(hw.createdAt).toLocaleDateString()}</span>
                    {hw.dueDate && <span className="text-orange-400">Due: {new Date(hw.dueDate).toLocaleDateString()}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          /* Homework Detail & Upload */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="col-span-1 lg:col-span-2">
              <div className="bg-white/5 border border-gold/10 rounded-xl p-8 mb-8">
                <h2 className="text-2xl font-serif font-bold text-gold-light mb-4 text-white">Instructions</h2>
                <div className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {selectedHomework.description}
                </div>
              </div>

              {/* Upload Section */}
              <div className="bg-white/5 border border-gold/10 rounded-xl p-8">
                <h2 className="text-xl font-bold text-gold-light mb-4">Submit Your Video</h2>
                {submission && submission.status === 'approved' ? (
                  <div className="p-4 bg-green-900/40 border border-green-500/30 rounded-lg text-green-400 flex items-center">
                    <FaCheckCircle className="mr-3 text-2xl" />
                    <div>
                      <h4 className="font-bold">Excellent!</h4>
                      <p className="text-sm text-green-200/80">Your submission has been approved.</p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="border-2 border-dashed border-gold/30 rounded-lg p-8 py-12 text-center hover:border-gold transition-colors bg-black/20">
                      <input
                        type="file"
                        accept="video/*"
                        onChange={handleFileChange}
                        className="hidden"
                        id="hw-video-upload"
                        disabled={uploading}
                      />
                      <label htmlFor="hw-video-upload" className="cursor-pointer block">
                        <FaVideo className="mx-auto text-5xl text-gold/50 mb-4" />
                        {file ? (
                          <div>
                            <p className="text-lg font-medium text-gold-light">{file.name}</p>
                            <p className="text-sm text-gold-light/50">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-lg font-medium text-gold-light mb-1">Click to select video</p>
                            <p className="text-sm text-gold-light/50">MP4, MOV, AVI, WEBM</p>
                          </div>
                        )}
                      </label>
                    </div>
                    
                    {uploading && (
                      <div className="bg-black/30 rounded-lg p-4 border border-gold/10">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gold-light">Uploading...</span>
                          <span className="text-sm text-gold">{uploadProgress}%</span>
                        </div>
                        <div className="w-full bg-black/50 rounded-full h-2">
                          <div
                            className="bg-gold h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {uploadStatus && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`p-4 rounded-lg flex items-center ${
                          uploadStatus === 'success' ? 'bg-green-900/40 text-green-400 border border-green-500/30' : 'bg-red-900/40 text-red-400 border border-red-500/30'
                        }`}
                      >
                        {uploadStatus === 'success' ? <FaCheckCircle className="mr-2" /> : <FaTimesCircle className="mr-2" />}
                        {uploadStatus === 'success' ? 'Video submitted successfully!' : 'Upload failed. Please try again.'}
                      </motion.div>
                    )}

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={uploading || !file}
                        className={`flex items-center px-8 py-3 rounded-xl font-bold transition-all ${
                          uploading || !file
                            ? 'bg-white/10 text-white/50 cursor-not-allowed'
                            : 'btn-gold shadow-lg shadow-orange-500/20'
                        }`}
                      >
                        <FaUpload className="mr-2" />
                        {uploading ? 'Uploading...' : (submission ? 'Resubmit Video' : 'Submit Video')}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>

            {/* Sidebar Status Info */}
            <div className="col-span-1">
              <div className="bg-white/5 border border-gold/10 rounded-xl p-6 sticky top-24">
                <h3 className="text-lg font-bold text-gold-light mb-4 border-b border-gold/10 pb-4">Submission Status</h3>
                
                {!submission ? (
                  <p className="text-gold-light/50 text-sm">Not submitted yet.</p>
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">Status:</span>
                      {getStatusBadge(submission.status)}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">Submitted:</span>
                      <span className="text-gray-300 text-sm">{new Date(submission.createdAt).toLocaleDateString()}</span>
                    </div>
                    {submission.videoUrl && (
                      <div className="mt-4">
                        <span className="text-gray-400 text-sm block mb-2">Attached Video:</span>
                        <video className="w-full rounded-lg bg-black/50" controls preload="metadata">
                          <source src={submission.videoUrl} type="video/mp4" />
                        </video>
                      </div>
                    )}
                    {submission.feedback && (
                      <div className="mt-6 p-4 bg-orange-900/20 border border-orange-500/20 rounded-lg">
                        <span className="text-orange-400 text-xs uppercase font-bold tracking-wider block mb-2">Teacher Feedback</span>
                        <p className="text-gray-300 text-sm italic">"{submission.feedback}"</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default StudentHomework;
